import { forwardRef, type HTMLAttributes } from 'react';
import { useMediaQuery } from 'react-responsive';
import { DialogVisuallyHidden, Drawer, DrawerContent, DrawerTitle } from '@zep-us/zus';
import { motion } from 'framer-motion';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { AiBuilderChatPanelContent } from './AiBuilderChatPanelContent';
import { AI_BUILDER_CHAT_DRAWER_BREAKPOINT, AI_BUILDER_CHAT_DRAWER_TOP_OFFSET, type AiBuilderShellTransitionState, getAiBuilderChatPanelVariants } from './AiBuilderShellTransition';

/**
 * 헤더 드롭다운(z-[70])·close confirm dialog(z-modal)와의 기존 스택 순서를
 * 유지하기 위해 ZUS 기본 dim(z-modal) 대신 백드롭을 z-[59]로 내린다.
 */
const AiBuilderDrawerDim = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((props, ref) => <div ref={ref} aria-hidden="true" data-ai-builder-chat-backdrop {...props} className="fixed inset-0 z-[59] bg-dimmed" />);
AiBuilderDrawerDim.displayName = 'AiBuilderDrawerDim';
export const AiBuilderChatPanel = ({
  transitionState
}: {
  transitionState: AiBuilderShellTransitionState;
}) => {
  const copy = useAiBuilderChatCopy();
  const usesDrawerLayout = useMediaQuery({
    maxWidth: AI_BUILDER_CHAT_DRAWER_BREAKPOINT
  });
  if (usesDrawerLayout) {
    return <Drawer open={transitionState !== 'closing'} dismissible={false}
    // modal이면 vaul이 body를 pointer-events:none으로 잠가 body로 portal되는
    // 헤더 드롭다운(z-[70])과 close confirm dialog 조작이 막힌다.
    // scroll lock은 useAiBuilderShellTransition의 useLockBodyScroll이 담당.
    modal={false} shouldScaleBackground={false}>
        <DrawerContent aria-describedby={undefined}
      // duration은 AI_BUILDER_SHELL_(CLOSE_)TRANSITION_DURATION_MS(360/240)와
      // 동기. 닫힘은 셸 reducer가 240ms 후 언마운트하므로 vaul 기본(500ms)을
      // 그대로 두면 exit 애니메이션이 중간에 잘린다.
      className="inset-x-0 overflow-hidden rounded-t-md border-none bg-default !ease-in-out ![transition-duration:360ms] data-[state=closed]:![transition-duration:240ms]" style={{
        height: `calc(100dvh - ${AI_BUILDER_CHAT_DRAWER_TOP_OFFSET}px)`,
        // tailwind-merge가 ZUS 기본 z-modal(400)과 z-[60]의 충돌을 인식하지
        // 못해 style로 고정한다. 헤더 드롭다운(z-[70])이 위에 떠야 한다.
        zIndex: 60
      }} CustomDim={AiBuilderDrawerDim}>
          <DialogVisuallyHidden>
            <DrawerTitle>{copy.panel.title}</DrawerTitle>
          </DialogVisuallyHidden>
          <AiBuilderChatPanelContent />
        </DrawerContent>
      </Drawer>;
  }
  return <motion.aside className="fixed bottom-0 right-0 top-0 z-[60] flex min-h-0 w-full flex-none flex-col bg-default tablet-sm:w-1/2 laptop-sm:w-[550px]" initial="closed" animate={transitionState} variants={getAiBuilderChatPanelVariants()} data-sentry-element="unknown" data-sentry-component="AiBuilderChatPanel" data-sentry-source-file="AiBuilderChatPanel.tsx">
      <AiBuilderChatPanelContent data-sentry-element="AiBuilderChatPanelContent" data-sentry-source-file="AiBuilderChatPanel.tsx" />
    </motion.aside>;
};